/**
 * Admin Announcements Component
 * Allows admins to broadcast messages to all users
 */
import React, { useState, useEffect } from 'react';
import { 
  Megaphone, Send, Loader2, Trash2, Clock, Users, 
  AlertCircle, Info, Wrench, Bell, CheckCircle, X
} from 'lucide-react';
import axios from '../../lib/axios';
import { toast } from 'sonner';
import { API, useAuth } from '../../App';

// Announcement type options
const ANNOUNCEMENT_TYPES = [
  { id: 'info', label: 'Information', icon: Info, color: 'blue', description: 'General information for users' },
  { id: 'update', label: 'Update', icon: CheckCircle, color: 'green', description: 'New features or improvements' },
  { id: 'maintenance', label: 'Maintenance', icon: Wrench, color: 'amber', description: 'Scheduled maintenance or downtime' },
  { id: 'alert', label: 'Alert', icon: AlertCircle, color: 'red', description: 'Important alerts requiring attention' },
];

const AdminAnnouncements = () => {
  const { token } = useAuth();
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [expandedMessages, setExpandedMessages] = useState(new Set()); // Track expanded messages
  
  // Form state
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [type, setType] = useState('info');
  const [link, setLink] = useState('');

  // Toggle expanded state for a message
  const toggleExpandMessage = (announcementId) => {
    setExpandedMessages(prev => {
      const newSet = new Set(prev);
      if (newSet.has(announcementId)) {
        newSet.delete(announcementId);
      } else {
        newSet.add(announcementId);
      }
      return newSet;
    });
  };

  // Check if a message is long enough to need expansion
  const isLongMessage = (msg) => {
    if (!msg) return false;
    return msg.length > 120;
  };
  
  // Fetch past announcements
  const fetchAnnouncements = async () => {
    try {
      const response = await axios.get(`${API}/admin/announcements`);
      setAnnouncements(response.data || []);
    } catch (error) {
      console.error('Failed to fetch announcements:', error);
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    fetchAnnouncements();
  }, [token]);
  
  // Send announcement
  const handleSend = async (e) => {
    e.preventDefault();
    
    if (!title.trim() || !message.trim()) {
      toast.error('Please fill in title and message');
      return;
    }
    
    setSending(true);
    try {
      const response = await axios.post(
        `${API}/admin/announcements/broadcast`,
        { title: title.trim(), message: message.trim(), type, link: link.trim() });
      
      toast.success(`Announcement sent to ${response.data.recipient_count} users`);
      
      // Reset form
      setTitle('');
      setMessage('');
      setType('info');
      setLink('');
      setShowForm(false);
      
      // Refresh list
      fetchAnnouncements();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send announcement');
    } finally {
      setSending(false);
    }
  };
  
  // Delete announcement
  const handleDelete = async (id) => {
    if (!window.confirm('Delete this announcement? This will also remove notifications from all users.')) {
      return;
    }
    
    try {
      await axios.delete(`${API}/admin/announcements/${id}`);
      toast.success('Announcement deleted');
      setAnnouncements(prev => prev.filter(a => a.id !== id));
    } catch (error) {
      toast.error('Failed to delete announcement');
    }
  };
  
  // Get type config
  const getTypeConfig = (typeId) => ANNOUNCEMENT_TYPES.find(t => t.id === typeId) || ANNOUNCEMENT_TYPES[0];
  
  // Format date
  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      month: 'short', day: 'numeric', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-foreground flex items-center gap-2">
            <Megaphone className="w-6 h-6 text-primary" />
            Broadcast Announcements
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            Send notifications to all users for updates or service announcements
          </p>
        </div>
        <button
          onClick={() => setShowForm(!showForm)}
          className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
        >
          {showForm ? <X className="w-4 h-4" /> : <Megaphone className="w-4 h-4" />}
          {showForm ? 'Cancel' : 'New Announcement'}
        </button>
      </div>
      
      {/* New Announcement Form */}
      {showForm && (
        <div className="bg-card border border-border rounded-xl p-6 animate-in fade-in slide-in-from-top-2 duration-200">
          <h3 className="text-lg font-semibold mb-4">Create New Announcement</h3>
          
          <form onSubmit={handleSend} className="space-y-4">
            {/* Announcement Type */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Type</label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {ANNOUNCEMENT_TYPES.map((t) => {
                  const Icon = t.icon;
                  const isSelected = type === t.id;
                  return (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => setType(t.id)}
                      className={`flex flex-col items-center gap-1.5 p-3 rounded-xl border-2 transition-all ${
                        isSelected 
                          ? `border-${t.color}-500 bg-${t.color}-500/10` 
                          : 'border-border hover:border-primary/50'
                      }`}
                    >
                      <Icon className={`w-5 h-5 ${isSelected ? `text-${t.color}-500` : 'text-muted-foreground'}`} />
                      <span className={`text-xs font-medium ${isSelected ? 'text-foreground' : 'text-muted-foreground'}`}>
                        {t.label}
                      </span>
                    </button>
                  );
                })}
              </div>
              <p className="text-xs text-muted-foreground mt-1.5">
                {getTypeConfig(type).description}
              </p>
            </div>
            
            {/* Title */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">Title *</label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g., Scheduled Maintenance Tonight"
                className="w-full px-4 py-2.5 bg-secondary border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50"
                maxLength={100}
                required
              />
            </div>
            
            {/* Message */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">Message *</label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Write your announcement message here..."
                rows={4}
                className="w-full px-4 py-2.5 bg-secondary border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50 resize-none"
                maxLength={500}
                required
              />
              <p className="text-xs text-muted-foreground mt-1 text-right">{message.length}/500</p>
            </div>
            
            {/* Optional Link */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">
                Link (optional)
              </label>
              <input
                type="text"
                value={link}
                onChange={(e) => setLink(e.target.value)}
                placeholder="e.g., /help/maintenance or https://..."
                className="w-full px-4 py-2.5 bg-secondary border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Users will be redirected here when clicking the notification
              </p>
            </div>
            
            {/* Submit Button */}
            <div className="flex items-center justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="px-4 py-2 text-muted-foreground hover:text-foreground transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={sending || !title.trim() || !message.trim()}
                className="flex items-center gap-2 px-6 py-2.5 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {sending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
                {sending ? 'Sending...' : 'Send to All Users'}
              </button>
            </div>
          </form>
        </div>
      )}
      
      {/* Past Announcements */}
      <div>
        <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">
          Recent Announcements
        </h3>
        
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-primary" />
          </div>
        ) : announcements.length === 0 ? (
          <div className="bg-card border border-border rounded-xl p-8 text-center">
            <Bell className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
            <p className="text-muted-foreground">No announcements sent yet</p>
            <p className="text-sm text-muted-foreground mt-1">
              Create your first announcement to notify all users
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {announcements.map((announcement) => {
              const typeConfig = getTypeConfig(announcement.type);
              const TypeIcon = typeConfig.icon;
              
              return (
                <div
                  key={announcement.id}
                  className="bg-card border border-border rounded-xl p-4 hover:border-primary/30 transition-colors"
                >
                  <div className="flex items-start gap-3">
                    {/* Type Icon */}
                    <div className={`w-10 h-10 rounded-lg bg-${typeConfig.color}-500/10 flex items-center justify-center flex-shrink-0`}>
                      <TypeIcon className={`w-5 h-5 text-${typeConfig.color}-500`} />
                    </div>
                    
                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-semibold text-foreground truncate">{announcement.title}</h4>
                        <span className={`px-2 py-0.5 text-xs rounded-full bg-${typeConfig.color}-500/10 text-${typeConfig.color}-600 dark:text-${typeConfig.color}-400`}>
                          {typeConfig.label}
                        </span>
                      </div>
                      
                      {/* Message with expandable text */}
                      <div>
                        <p className={`text-sm text-muted-foreground ${
                          !expandedMessages.has(announcement.id) && isLongMessage(announcement.message)
                            ? 'line-clamp-2'
                            : ''
                        }`}>
                          {announcement.message}
                        </p>
                        {isLongMessage(announcement.message) && (
                          <button
                            onClick={() => toggleExpandMessage(announcement.id)}
                            className="text-xs font-medium text-primary hover:text-primary/80 mt-1 transition-colors"
                          >
                            {expandedMessages.has(announcement.id) ? 'Lees minder ▲' : 'Lees meer ▼'}
                          </button>
                        )}
                      </div>
                      
                      {/* Meta Info */}
                      <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {formatDate(announcement.created_at)}
                        </span>
                        <span className="flex items-center gap-1">
                          <Users className="w-3 h-3" />
                          {announcement.recipient_count} recipients
                        </span>
                        <span>by {announcement.created_by_name}</span>
                      </div>
                    </div>
                    
                    {/* Delete Button */}
                    <button
                      onClick={() => handleDelete(announcement.id)}
                      className="p-2 text-muted-foreground hover:text-red-500 hover:bg-red-500/10 rounded-lg transition-colors flex-shrink-0"
                      title="Delete announcement"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminAnnouncements;
